import { ChangeEvent, Dispatch, FC, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import Modal from "../../components/shared/Modal/Modal";
import { useTranslation } from "react-i18next";
import { ServiceNextProject } from "../../services/ServiceNextProject";
import { Loading } from "../../components/shared";
import { NotificationManager } from "react-notifications";
import styles from './ImportNextProjectModal.module.scss';
import TooltipContainer from "../../components/shared/TooltipContainer/TooltipContainer";
import StyledCheckbox from "../../components/shared/StyledCheckbox/StyledCheckbox";
import SearchInput from "../../components/shared/SearchInput";
import BtButton from "../bt-button/bt-button";
import Badge from "../../components/shared/Badge/Badge";
import { ReactComponent as RefreshIcon } from "../../assets/svg/refreshIcon.svg";

type Props = {
  show: boolean,
  setShow: Dispatch<SetStateAction<boolean>>,
  allNextProjects: any[];
  fetchAllNextProjects: () => void,
}

type Node = {
  id: number,
  name: string,
  number: string,
  parent: number,
  existing?: string,
  children: Node[],
}

const ImportNextProjectModal : FC<Props> = ({show, setShow, allNextProjects, fetchAllNextProjects }) => {
  const [t] = useTranslation('projects');
  const [projectTree, setProjectTree] = useState<Node>();
  const [selectedProjects, setSelectedProjects] = useState<Set<number>>(new Set());
  const [expandedProjects, setExpandedProjects] = useState<Set<number>>(new Set());
  const [searchTerm, setSearchTerm] = useState<string>('');

  useEffect(() => {
    if (show && allNextProjects.length == 0) {
      fetchAllNextProjects();
    }
  }, [show]);

  useEffect(() => {
    if (show) {      
      (async () => {
        try {
          const nodes = new Map<number, Node>();
          const root = {
              id: 0,
              name: "root",
              number: "",
              parent: -1,
              children: [],
          };
          nodes.set(0, root);
          for (const project of allNextProjects) {
            nodes.set(project.id, {
              id: project.id,
              name: project.name,
              number: project.projectno,
              parent: project.parentprojectid ?? 0,
              existing: project.existing,
              children: [],
            });
          }
          for (const project of allNextProjects) {
            const node = nodes.get(project.id);
            if (node) {
              const parent = nodes.get(node.parent);
              if (parent) {
                parent.children.push(node);
              }
            }
          }
          setProjectTree(root);
        } catch (error) {
          console.log(error);
        }
      })();
    }
  }, [show, allNextProjects]);

  const nodeContainsText = useCallback((node: Node, text: string) : boolean => {
    return node.name.toLowerCase().includes(text) || 
      node.children.some(child => nodeContainsText(child, text));
  }, []);

  const renderTree = (node: Node, depth = 0) => {
    if (!nodeContainsText(node, searchTerm.toLowerCase())) return null;
    return <div key={node.id} className={styles.node}> 
        {node.children.length > 0 ? (
          <div style={{lineHeight: '24px', cursor: 'pointer', display: 'flex', alignItems: 'center'}} onClick={() => {
            if (expandedProjects.has(node.id)) {
              expandedProjects.delete(node.id);
            } else {
              expandedProjects.add(node.id);
            }
            setExpandedProjects(new Set(expandedProjects));
          }}>
            <span className={styles.expandCollapse}>{expandedProjects.has(node.id) ? '-' : '+'}</span>
              <label onClick={e => e.stopPropagation()} style={{display:"inline-flex"}}>
                <StyledCheckbox 
                  checked={selectedProjects.has(node.id)}
                  onChange={() => {
                    if (selectedProjects.has(node.id)) {
                      selectedProjects.delete(node.id);
                    } else {
                      selectedProjects.add(node.id);
                    }
                    setSelectedProjects(new Set(selectedProjects));
                  }}
                  style={{ padding: 0 }}
                />{" "}
                {node.number} {node.name}
              </label>
          </div>
        ) : (
          <div style={{lineHeight: '24px'}} className={styles.leaf}>
            <label>
              <StyledCheckbox 
                checked={selectedProjects.has(node.id)}
                onChange={() => {
                  if (selectedProjects.has(node.id)) {
                    selectedProjects.delete(node.id);
                  } else {
                    selectedProjects.add(node.id);
                  }
                  setSelectedProjects(new Set(selectedProjects));
                }}
                style={{ padding: 0, marginLeft: "22px" }}
              />{" "}
              {node.number} {node.name} {node.existing && (
                <TooltipContainer renderReferenceComponent={(className, ref) =>
                  <Badge><span className={className} ref={ref}>{t('importNextProjectModal.existing', 'finns redan')}</span></Badge>
                }>
                  <div className={styles.existing}>
                    {t(
                      'importNextProjectModal.existingTooltip', 
                      `Projektet är redan länkat till projektet {{existing}}.
                      
                      Om du importerar projeket igen kommer eventuell slutkostnad uppdateras och inköp vars konto matchar en kategori som inte redan finns i Accurator skapas.`, 
                      { existing: node.existing }
                    )}
                  </div>
                </TooltipContainer>
              )}
            </label>
          </div>
        )}
      {expandedProjects.has(node.id) && node.children.map(child => renderTree(child, depth + 1))}
    </div>
  }

  const importProjects = useCallback(async () => {
    if (selectedProjects.size > 0) {
      const response = await ServiceNextProject.importProjects(Array.from(selectedProjects));
      if (response.missingAccounts && response.missingAccounts.length > 0) {
        NotificationManager.warning(
          t(
            'importNextProjectModal.warning.missingAccounts.text', 
            'Projekten har importerats, men kategorier saknas för följande konton: {{missingAccounts_list}}.',
            { missingAccounts: response.missingAccounts },             
          ), 
          t('importNextProjectModal.warning.missingAccounts.headline', 'Importerat')
        );
      } else {
        NotificationManager.success(
          t('importNextProjectModal.success.text', 'Projekten har importerats'), 
          t('importNextProjectModal.success.headline', 'Importerat')
        );
      }
      
      setShow(false);                
    }
  }, [projectTree]);

  const clearSelection = useCallback(() => {
    setSelectedProjects(new Set());
  }, []);

  return <Modal
      title={t('importNextProjectModal.headline', 'Importera från Next Project')} 
      show={show}
      setShow={setShow}
      buttonInfo={{
        label: t('importNextProjectModal.button.import', 'Importera'), 
        action: importProjects,
        disabled: selectedProjects.size === 0,
      }}
      containerBeforeButtons={
        <div className={styles.footer}>
          <BtButton color="white" onClick={clearSelection}>{t('importNextProjectModal.button.clear', 'Rensa')}</BtButton>
          <div>{selectedProjects.size} {t('importNextProjectModal.selectedProjects', 'valda')}</div>
        </div>
      }      
    >
       
        <div className={styles.search}>
          <div>
            <SearchInput
              placeholder={t('importNextProjectModal.search', 'Sök')}
              value={searchTerm}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)} 
              className={undefined}
            />
          </div>
          <BtButton
            onClick={fetchAllNextProjects}
            color="white"
          >
            <RefreshIcon />
          </BtButton>
          
        </div>
        <div className={styles.tree}>
          {allNextProjects.length == 0 ? (
            <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%'}}>
              <Loading type="inline" color="#ff5e15" size="30px" />
            </div>
          ) : (
            projectTree && projectTree && projectTree.children.map(child => renderTree(child, 0))
          )}          
        </div>
      
    </Modal>
};

export default ImportNextProjectModal;
